import { Injectable, TemplateRef } from '@angular/core';
import { filterAllTruthyInTuple, subjectFromObs } from 'core/rx-js';
import { LoaderService, ScreenUtilsService } from 'core/services';
import { BehaviorSubject, Observable, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, fromEvent, map, merge, switchMap, take, takeUntil } from 'rxjs';
import { DsgDeepTranslationKey } from '../../pipes/translate.pipe';
import { ToolbarSearchService } from '../../root/sidenav/toolbar-search/toolbar-search.service';
import { IColumn } from './data-grid.component';

export type LoadFn = (skip: number, take: number, search: string) => Observable<{ data: any[], totalCount: number }>
export type GetRowStyleFn = (rowData: any) => any;
export type GetRowLoaderKeysFn = (rowData: any) => string[]
export type CustomizeRowFn = (element: HTMLElement, rowData: any) => void

export type UpdateDataFn = (getPrevItem: (key: any) => any) => Observable<{ type: 'add' | 'remove' | 'update', payload: any }>

export interface ICollapsedColumn {
  dataField: string,
  caption: DsgDeepTranslationKey
  fields: string[],
  rowCount: number
}


@Injectable()
export class DataGridService {
  private readonly _destroy = new Subject<void>()

  readonly loadFnSubject = new BehaviorSubject<LoadFn>(undefined)
  readonly getRowLoaderKeysSubject = new BehaviorSubject<GetRowLoaderKeysFn>(undefined)

  readonly pageSize = 20;
  readonly pageIdxSubject = new BehaviorSubject<number>(0);
  readonly totalLengthSubject = new BehaviorSubject<number>(2000);
  readonly pagedDataSubject = new BehaviorSubject<Record<number, any[]>>({})
  get #pagedData() {
    return this.pagedDataSubject.value;
  }

  readonly keyFieldSubject = new BehaviorSubject<string>('id');
  get #keyField() {
    return this.keyFieldSubject.value
  }

  readonly columnsSubject = new BehaviorSubject<IColumn[]>(undefined)
  readonly searchSubject = this._toolbarSearchService.searchSubject;
  readonly searchObs = this.searchSubject.pipe(debounceTime(300), distinctUntilChanged())
  readonly useActionsSubject = new BehaviorSubject<boolean>(false);
  readonly actionsTemplateSubject = new BehaviorSubject<TemplateRef<any>>(null);
  readonly customizeRowSubject = new BehaviorSubject<CustomizeRowFn>(null)
  readonly cellTemplatesSubject = new BehaviorSubject<{ [key: string]: TemplateRef<any> }>(null);
  readonly scrollerSubject = new BehaviorSubject<HTMLElement>(null);
  readonly showSearchSubject = new BehaviorSubject<boolean>(false);
  readonly mobileItemTemplateSubject = new BehaviorSubject<TemplateRef<null>>(null)
  get #scroller() {
    return this.scrollerSubject.value
  }

  readonly updateDataFnSubject = new BehaviorSubject<UpdateDataFn>(null)
  readonly toolbarTemplateSubject = new BehaviorSubject<TemplateRef<any>>(null);

  readonly showCollapsedModeSubject = subjectFromObs({
    initialValue: false,
    observable: this._screenUtilsService.isSmallerScreenObs,
    until: this._destroy
  })
  get #showCollapsedMode() {
    return this.showCollapsedModeSubject.value
  }

  readonly showSearchObs = combineLatest([this.showSearchSubject, this.showCollapsedModeSubject]).pipe(
    map(([showSearch, showCollapsedMode]) => showSearch && showCollapsedMode),
    distinctUntilChanged()
  )

  readonly showHeaderObs = combineLatest([this.toolbarTemplateSubject, this.showSearchObs]).pipe(
    map(([toolbarTemplate, showSearch]) => !!toolbarTemplate || showSearch),
    distinctUntilChanged()
  )

  readonly columnsObs = combineLatest([
    this.columnsSubject,
    this.actionsTemplateSubject,
    this.showCollapsedModeSubject
  ])
    .pipe(
      filter(([columns]) => !!columns),
      map(([columns, actionsTemplate, showCollapsedMode]): IColumn[] => {
        columns = columns.filter((col) => col.caption);
        if (showCollapsedMode) {
          columns = columns.filter((col) => col.showInCollapsed || col.title)
        }

        if (!actionsTemplate) {
          return columns
        }

        return [...columns, { caption: '', dataField: 'actions', type: 'actions' }]
      }
      )
    )

  readonly scrollEndReachedObs = this.scrollerSubject.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    switchMap((scroller) => merge(
      fromEvent(scroller, 'scroll'),
      this.displayedDataSubject.pipe(
        filter(data => !!data?.length),
        debounceTime(100)
      )
    ).pipe(
      map(() => (scroller.scrollTop + scroller.clientHeight) > (scroller.scrollHeight - 100)))
    ),
    distinctUntilChanged()
  )

  readonly displayedDataSubject = subjectFromObs({
    initialValue: null,
    observable: this.pagedDataSubject.pipe(
      filter(Boolean),
      map((pagedData) => Object.entries(pagedData)
        .sort(([prevIdx], [nextIdx]) => Number(prevIdx) - Number(nextIdx))
        .map(([__pageIdx, pagedArr]) => pagedArr.map((item) => ({ ...item, __pageIdx: Number(__pageIdx) })))
        .flat()
      ),
      distinctUntilChanged()
    )
  })


  constructor(
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _screenUtilsService: ScreenUtilsService,
    private readonly _toolbarSearchService: ToolbarSearchService

  ) {


    this._subscribeToDataFetch();
    this._initScrollSubscription();
    this._subscribeToUpdateDataFn();

    this._toolbarSearchService.searchVisibleSubject.next(true);
  }

  private _subscribeToDataFetch() {
    merge(
      this.loadFnSubject.pipe(distinctUntilChanged()),
      this.searchObs
    )
      .pipe(takeUntil(this._destroy))
      .subscribe(() => {
        this.pageIdxSubject.next(0);
        this.pagedDataSubject.next({});
      })

    combineLatest([this.pageIdxSubject, this.loadFnSubject, this.searchObs])
      .pipe(

        takeUntil(this._destroy),
        debounceTime(100),
        filter(([pageIdx, loadFn]) => Boolean(loadFn) && !this.#pagedData[pageIdx]),
        switchMap(([pageIdx, load, search]) => this._fetchData(pageIdx, load, search)
          .pipe(take(1))
        )
      )
      .subscribe(({ data, totalCount }) => {

        this.totalLengthSubject.next(totalCount);
        this._setPagedData(this.pageIdxSubject.value, data);
        if (this.#showCollapsedMode) {
          setTimeout(() => {
            if (!this.#scroller) {
              return;
            }
            this.#scroller.scrollTo({ top: this.#scrollTop });
          })

          return;
        }

      })
  }

  readonly #getPrevItem = (key: any) => Object.values(this.#pagedData)
    .flat()
    .find((item) => item[this.#keyField] === key)

  private _subscribeToUpdateDataFn() {

    combineLatest([this.updateDataFnSubject, this.keyFieldSubject])
      .pipe(
        filterAllTruthyInTuple(),
        switchMap(([updateDataFn, keyField]) => updateDataFn(this.#getPrevItem).pipe(
          map(({ payload, type }) => ({ payload, type, key: payload[keyField] }))
        )),
        takeUntil(this._destroy)
      )
      .subscribe(({ key, payload, type }) => {

        if (type === 'update') {
          const replaced = this._findAndReplaceItem(key, payload);
          if (replaced) {
            this.pagedDataSubject.next(this.#pagedData);
          }
          return;
        }


        if (type === 'add') {
          if (!this.#pagedData[-1]) {
            this.#pagedData[-1] = [];
          }

          this.#pagedData[-1].push(payload);
          this.pagedDataSubject.next(this.#pagedData);
          return;
        }

        if (type === 'remove') {
          this.refresh('displayedPages');
          return;
        }

      })
  }




  private _findAndReplaceItem(key: any, data: any) {

    for (const arr of Object.values(this.#pagedData)) {
      const idx = arr.findIndex((item) => item[this.#keyField] === key);
      if (idx !== -1) {
        arr[idx] = data;
        return true;
      }
    }

    return false;
  }


  private _fetchData(pageIdx: number, loadFn: LoadFn, search: string) {
    this._loaderService.hideByKey('grid.load')
    const id = this._loaderService.show({ key: 'grid.load', text: 'ss-general.loading' })

    return loadFn(pageIdx * this.pageSize, this.pageSize, search)
      .pipe(
        this._loaderService.disposeLoader(id),
        this._loaderService.disposeByKey('grid.load')
      )


  }

  private _setPagedData(pageIdx: number, data: any[]) {

    this.pagedDataSubject.next({
      ...this.pagedDataSubject.value,
      [pageIdx]: data
    })


  }

  #scrollTop: number;
  private _initScrollSubscription() {

    this.scrollEndReachedObs.pipe(
      filter(Boolean),
      debounceTime(200),
      switchMap(() =>
        this.pagedDataSubject.pipe(
          map((pagedData) => Object.keys(pagedData).map(Number)),
          filter((pageIdxArr) => !!pageIdxArr.length),
          map((pageIdxArr) => Math.max(...pageIdxArr)),
          take(1)
        )
      ),
      takeUntil(this._destroy)
    )
      .subscribe((pageIdx) => {

        if ((pageIdx + 1) * this.pageSize >= this.totalLengthSubject.value) {

          return;
        }

        this.#scrollTop = this.#scroller.scrollTop;
        this.pageIdxSubject.next(pageIdx + 1)

      })



  }


  refresh(refreshType: 'hard' | 'displayedPages' | 'currentPage' = 'displayedPages') {


    if (refreshType === 'hard') {
      this.pagedDataSubject.next({});
      this.pageIdxSubject.next(0);

      this._fetchData(this.pageIdxSubject.value, this.loadFnSubject.value, this.searchSubject.value)
        .pipe(take(1))
        .subscribe(({ data, totalCount }) => {

          this._setPagedData(this.pageIdxSubject.value, data)
          this.totalLengthSubject.next(totalCount);
        })

      return;
    }


    if (refreshType === 'currentPage') {
      this._fetchData(this.pageIdxSubject.value, this.loadFnSubject.value, this.searchSubject.value)
        .pipe(take(1))
        .subscribe(({ data, totalCount }) => {

          this._setPagedData(this.pageIdxSubject.value, data)
          this.totalLengthSubject.next(totalCount);
        })
      return
    }


    combineLatest(
      Object.keys(this.#pagedData)
        .map(Number)
        .filter((pageIdx) => pageIdx >= 0)
        .map((pageIdx) =>
          this._fetchData(pageIdx, this.loadFnSubject.value, this.searchSubject.value)
            .pipe(
              map((response) => ({
                pageIdx,
                response
              }))
            )
        )
    )
      .pipe(
        map((arr) => ({
          pagedData: Object.fromEntries(
            arr.map(({ pageIdx, response }) => [pageIdx, response.data] as const)
          ),
          totalCount: arr.at(0).response.totalCount
        })
        ),
        take(1)
      )
      .subscribe(({ pagedData, totalCount }) => {
        this.pagedDataSubject.next(pagedData);
        this.totalLengthSubject.next(totalCount)
      })


  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
    this._toolbarSearchService.searchVisibleSubject.next(false);
  }
}
